import { useCallback, useEffect, useRef, useState } from 'react';
import {
    fetchAlerts,
    fetchUnreadCount,
    markAlertSeen,
    ignoreAlert,
    silenceAlert,
    resolveAlert,
} from './alertsApi';

const POLL_MS = 600_000; // 10 minutos (chequeo de fondo, solo el contador)
const PER_PAGE = 500; // Traer TODAS en una sola página; el panel no tiene navegación de páginas.

// Prioridad para ordenar: las "sin tocar" (open) primero, luego el resto.
const STATUS_ORDER = { open: 0, silenced: 1, ignored: 2, resolved: 3 };

// Descarta argumentos que no son params reales (p. ej. el event de un onClick,
// que llegaba serializado como ?_reactName=onClick&type=click&...).
function sanitizeParams(params) {
    if (!params || typeof params !== 'object') return {};
    if (params.nativeEvent || params._reactName || typeof params.preventDefault === 'function') return {};
    return params;
}

// Ordena dejando las alertas sin atender (open) al principio; dentro de cada
// grupo, las más recientes primero.
function sortUntouchedFirst(list) {
    return [...list].sort((a, b) => {
        const pa = STATUS_ORDER[a.status] ?? 9;
        const pb = STATUS_ORDER[b.status] ?? 9;
        if (pa !== pb) return pa - pb;
        return new Date(b.detected_at) - new Date(a.detected_at);
    });
}

export default function useAlerts() {
    const [alerts, setAlerts] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // pending[id] = 'seen' | 'ignore' | 'silence' | 'resolve' mientras la acción está en vuelo.
    const [pending, setPending] = useState({});
    const intervalRef = useRef(null);

    const refetch = useCallback(async (params = {}) => {
        setLoading(true);
        setError(null);
        try {
            const [{ data: listRes }, { data: countRes }] = await Promise.all([
                fetchAlerts({ per_page: PER_PAGE, ...sanitizeParams(params) }),
                fetchUnreadCount(),
            ]);
            setAlerts(sortUntouchedFirst(listRes?.data ?? []));
            setUnreadCount(countRes?.unread_count ?? 0);
        } catch (e) {
            setError(e?.response?.data?.message || e.message || 'Error');
        } finally {
            setLoading(false);
        }
    }, []);

    // Chequeo ligero de fondo: solo el contador (numerito rojo), sin traer la lista.
    const refreshCount = useCallback(async () => {
        try {
            const { data: countRes } = await fetchUnreadCount();
            setUnreadCount(countRes?.unread_count ?? 0);
        } catch (e) {
            // Silencioso: es solo el badge de fondo.
        }
    }, []);

    const markPending = useCallback((id, action) => {
        setPending((p) => ({ ...p, [id]: action }));
    }, []);
    const clearPending = useCallback((id) => {
        setPending((p) => {
            const next = { ...p };
            delete next[id];
            return next;
        });
    }, []);

    const startPolling = useCallback(() => {
        if (intervalRef.current) return;
        intervalRef.current = setInterval(refreshCount, POLL_MS);
    }, [refreshCount]);

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        refreshCount(); // Al cargar: solo el contador (ligero). La lista se trae al abrir la campana/panel.
        startPolling();

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                refreshCount();
                stopPolling();
                startPolling();
            } else {
                stopPolling();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [refreshCount, startPolling, stopPolling]);

    const runAction = useCallback(async (id, action, fn) => {
        if (pending[id]) return;
        markPending(id, action);
        try {
            await fn();
            await refetch();
        } catch (e) {
            setError(e?.response?.data?.message || e.message || 'Error');
        } finally {
            clearPending(id);
        }
    }, [refetch, pending, markPending, clearPending]);

    const markSeen = useCallback((id) => runAction(id, 'seen', () => markAlertSeen(id)), [runAction]);
    const ignore = useCallback((id) => runAction(id, 'ignore', () => ignoreAlert(id)), [runAction]);
    const silence = useCallback((id, hours) => runAction(id, 'silence', () => silenceAlert(id, hours)), [runAction]);
    const resolve = useCallback((id) => runAction(id, 'resolve', () => resolveAlert(id)), [runAction]);

    return {
        alerts,
        unreadCount,
        loading,
        error,
        pending,
        refetch,
        markSeen,
        ignore,
        silence,
        resolve,
    };
}
